import { Color, Random } from 'excalibur';
import { toRadians } from './angles';
import { Palette } from './palette';

export function withAlpha(color: Color, alpha: number): Color {
  return new Color(color.r, color.g, color.b, alpha);
}

export function css(color: Color, alpha?: number): string {
  const a = alpha === undefined ? color.a : alpha;
  return `rgba(${Math.round(color.r)},${Math.round(color.g)},${Math.round(color.b)},${a})`;
}

interface Harmonic {
  amplitude: number;
  frequency: number;
  phase: number;
}

function harmonic(rng: Random, amplitude: number, minFrequency: number, maxFrequency: number): Harmonic {
  return {
    amplitude,
    frequency: rng.integer(minFrequency, maxFrequency),
    phase: rng.floating(0, Math.PI * 2),
  };
}

function sumHarmonics(harmonics: Harmonic[], angle: number): number {
  let value = 0;
  for (const h of harmonics) {
    value += Math.sin(angle * h.frequency + h.phase) * h.amplitude;
  }
  return value;
}

export function wobblyCirclePath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  rng: Random,
  wobble = 3,
  segments = 64,
  overshoot = 0.05,
): void {
  const harmonics = [harmonic(rng, wobble, 2, 4), harmonic(rng, wobble * 0.5, 5, 9)];
  const total = Math.PI * 2 + overshoot;
  ctx.beginPath();
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * total;
    const r = radius + sumHarmonics(harmonics, angle);
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
}

export function wobblyArcPath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
  rng: Random,
  wobble = 2.5,
  segments = 32,
): void {
  const span = endAngle - startAngle;
  const total = span + Math.sign(span) * Math.min(0.12, Math.abs(span) * 0.08);
  const harmonics = [harmonic(rng, wobble, 2, 4), harmonic(rng, wobble * 0.5, 5, 8)];
  ctx.beginPath();
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = startAngle + total * t;
    const r = radius + sumHarmonics(harmonics, angle);
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
}

export function wobblyLinePath(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  rng: Random,
  wobble = 2,
  segments = 8,
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy) || 1;
  const nx = -dy / length;
  const ny = dx / length;
  const harmonics = [harmonic(rng, wobble, 1, 3)];
  ctx.beginPath();
  for (let i = 0; i <= segments; i++) {
    const t = -0.02 + (i / segments) * 1.04;
    const offset = sumHarmonics(harmonics, t * Math.PI * 2);
    const x = x1 + dx * t + nx * offset;
    const y = y1 + dy * t + ny * offset;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
}

export interface SketchStyle {
  color: Color;
  width?: number;
  alpha?: number;
  cap?: CanvasLineCap;
}

export function sketchFill(
  ctx: CanvasRenderingContext2D,
  style: SketchStyle,
  path: (c: CanvasRenderingContext2D) => void,
): void {
  ctx.save();
  ctx.fillStyle = css(style.color, style.alpha === undefined ? style.color.a : style.alpha);
  path(ctx);
  ctx.fill();
  ctx.restore();
}

export function sketchStroke(
  ctx: CanvasRenderingContext2D,
  rng: Random,
  style: SketchStyle,
  path: (c: CanvasRenderingContext2D) => void,
  passes = 2,
  offset = 1.4,
): void {
  const baseAlpha = style.alpha === undefined ? 1 : style.alpha;
  const baseWidth = style.width === undefined ? 3 : style.width;
  for (let pass = 0; pass < passes; pass++) {
    ctx.save();
    ctx.lineCap = style.cap === undefined ? 'round' : style.cap;
    ctx.lineJoin = 'round';
    if (pass === 0) {
      ctx.lineWidth = baseWidth;
      ctx.globalAlpha = baseAlpha;
    } else {
      ctx.translate(rng.floating(-offset, offset), rng.floating(-offset, offset));
      ctx.rotate(rng.floating(-0.01, 0.01));
      ctx.lineWidth = baseWidth * 0.65;
      ctx.globalAlpha = baseAlpha * 0.5;
    }
    ctx.strokeStyle = css(style.color, 1);
    path(ctx);
    ctx.stroke();
    ctx.restore();
  }
}

export function drawTaperedStroke(
  ctx: CanvasRenderingContext2D,
  rng: Random,
  x: number,
  yStart: number,
  yEnd: number,
  thickness: number,
  body: Color,
  edge: Color,
): void {
  const segments = 20;
  const centerline = [harmonic(rng, 1.8, 1, 3), harmonic(rng, 0.9, 4, 7)];
  const left: Array<[number, number]> = [];
  const right: Array<[number, number]> = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const y = yStart + (yEnd - yStart) * t;
    const cx = x + sumHarmonics(centerline, t * Math.PI * 2);
    const profile = t < 0.75 ? 1 - 0.1 * (t / 0.75) : 0.9 - 0.5 * ((t - 0.75) / 0.25);
    const half = (thickness * profile) / 2;
    const edgeNoise = sumHarmonics(centerline, t * Math.PI * 2 + 1.7) * 0.4;
    left.push([cx - half + edgeNoise, y]);
    right.push([cx + half - edgeNoise, y]);
  }
  const tipX = (left[segments][0] + right[segments][0]) / 2;
  const tipY = left[segments][1];
  const tipRadius = Math.max(3, thickness * 0.34);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(left[0][0], left[0][1]);
  for (const point of left) {
    ctx.lineTo(point[0], point[1]);
  }
  ctx.arc(tipX, tipY, tipRadius, Math.PI, Math.PI * 2, false);
  for (let i = segments; i >= 0; i--) {
    ctx.lineTo(right[i][0], right[i][1]);
  }
  ctx.closePath();
  ctx.fillStyle = css(body, 0.95);
  ctx.fill();
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = css(edge, 0.45);
  ctx.stroke();
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = 0.16;
  ctx.lineCap = 'round';
  ctx.strokeStyle = css(body.lighten(0.55), 1);
  for (let streak = -1; streak <= 1; streak++) {
    ctx.beginPath();
    for (let i = 0; i <= 8; i++) {
      const t = 0.1 + (i / 8) * 0.75;
      const y = yStart + (yEnd - yStart) * t;
      const px = x + streak * thickness * 0.22 + sumHarmonics(centerline, t * Math.PI * 2 + streak) * 0.3;
      if (i === 0) {
        ctx.moveTo(px, y);
      } else {
        ctx.lineTo(px, y);
      }
    }
    ctx.lineWidth = 1.4;
    ctx.stroke();
  }
  ctx.restore();

  ctx.save();
  ctx.fillStyle = css(body, 0.98);
  wobblyCirclePath(ctx, tipX, tipY, tipRadius + 1.5, rng, 1.2, 20);
  ctx.fill();
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = css(edge, 0.5);
  ctx.stroke();
  ctx.restore();
}

export function drawHeart(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  rng: Random,
  fill: Color,
  outline: Color,
  alpha = 0.95,
): void {
  const lobeRadius = size * 0.3;
  const leftX = cx - size * 0.23 + rng.floating(-0.6, 0.6);
  const rightX = cx + size * 0.21 + rng.floating(-0.6, 0.6);
  const lobeY = cy - size * 0.16 + rng.floating(-0.5, 0.5);
  const apexY = cy + size * 0.52;
  const shoulderY = lobeY + lobeRadius * 0.45;

  ctx.save();
  ctx.fillStyle = css(fill, alpha);
  wobblyCirclePath(ctx, leftX, lobeY, lobeRadius, rng, size * 0.03, 24);
  ctx.fill();
  wobblyCirclePath(ctx, rightX, lobeY - size * 0.02, lobeRadius * 1.06, rng, size * 0.03, 24);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(leftX - lobeRadius * 0.92, shoulderY);
  ctx.lineTo(cx + rng.floating(-2, 2), apexY + rng.floating(-2, 2));
  ctx.lineTo(rightX + lobeRadius * 0.92, shoulderY - size * 0.02);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  const lineWidth = Math.max(1.6, size * 0.055);
  sketchStroke(ctx, rng, { color: outline, width: lineWidth, alpha: 0.85 }, (c) =>
    wobblyCirclePath(c, leftX, lobeY, lobeRadius, rng, size * 0.03, 24), 1);
  sketchStroke(ctx, rng, { color: outline, width: lineWidth, alpha: 0.85 }, (c) =>
    wobblyCirclePath(c, rightX, lobeY - size * 0.02, lobeRadius * 1.06, rng, size * 0.03, 24), 1);
  const triangleStyle: SketchStyle = { color: outline, width: lineWidth, alpha: 0.85 };
  sketchStroke(ctx, rng, triangleStyle, (c) =>
    wobblyLinePath(c, leftX - lobeRadius * 0.92, shoulderY, cx, apexY, rng, 1.5, 4), 1);
  sketchStroke(ctx, rng, triangleStyle, (c) =>
    wobblyLinePath(c, cx, apexY, rightX + lobeRadius * 0.92, shoulderY, rng, 1.5, 4), 1);
}

export function drawSparkle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  rng: Random,
  color: Color,
  alpha = 0.9,
): void {
  const style: SketchStyle = { color, width: Math.max(1.4, size * 0.16), alpha };
  sketchStroke(ctx, rng, style, (c) => wobblyLinePath(c, cx - size, cy, cx + size, cy, rng, size * 0.12, 3), 1);
  sketchStroke(ctx, rng, style, (c) => wobblyLinePath(c, cx, cy - size, cx, cy + size, rng, size * 0.12, 3), 1);
}

export function drawPaperTexture(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  rng: Random,
  tint: Color,
  density = 2400,
): void {
  const count = Math.round((width * height) / density);
  for (let i = 0; i < count; i++) {
    const x = rng.floating(0, width);
    const y = rng.floating(0, height);
    const radius = rng.floating(0.4, 1.6);
    ctx.fillStyle = css(tint, rng.floating(0.02, 0.07));
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  const fibers = Math.round(count / 4);
  ctx.save();
  ctx.lineCap = 'round';
  for (let i = 0; i < fibers; i++) {
    const x = rng.floating(0, width);
    const y = rng.floating(0, height);
    const length = rng.floating(6, 26);
    const angle = rng.floating(0, Math.PI * 2);
    ctx.strokeStyle = css(tint, rng.floating(0.008, 0.02));
    ctx.lineWidth = rng.floating(0.6, 1.6);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(angle) * length, y + Math.sin(angle) * length);
    ctx.stroke();
  }
  ctx.restore();
}

export function drawVignette(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  color: Color,
  strength = 0.2,
): void {
  const gradient = ctx.createRadialGradient(
    width / 2,
    height / 2,
    Math.min(width, height) * 0.22,
    width / 2,
    height / 2,
    Math.max(width, height) * 0.62,
  );
  gradient.addColorStop(0, css(color, 0));
  gradient.addColorStop(1, css(color, strength));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

export type Expression =
  | 'idle'
  | 'happy'
  | 'sad'
  | 'wow'
  | 'grin'
  | 'grit'
  | 'dizzy'
  | 'fire'
  | 'open'
  | 'tongue'
  | 'wink'
  | 'derp';

export const FUNNY_EXPRESSIONS: Expression[] = [
  'happy',
  'wow',
  'grin',
  'grit',
  'dizzy',
  'open',
  'tongue',
  'wink',
  'derp',
  'sad',
];

export function drawFlame(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  height: number,
  width: number,
  rng: Random,
  core: Color,
  outer: Color,
  alpha = 1,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.globalAlpha = alpha;
  const sway = rng.floating(-width * 0.45, width * 0.45);
  sketchFill(ctx, { color: outer, alpha: 0.5 }, (c) => {
    c.beginPath();
    c.moveTo(-width / 2, 0);
    c.bezierCurveTo(-width * 0.78, -height * 0.32, -width * 0.32, -height * 0.74, sway, -height);
    c.bezierCurveTo(width * 0.32, -height * 0.74, width * 0.78, -height * 0.32, width / 2, 0);
    c.closePath();
  });
  sketchFill(ctx, { color: core, alpha: 0.85 }, (c) => {
    c.beginPath();
    c.moveTo(-width * 0.27, 0);
    c.bezierCurveTo(-width * 0.42, -height * 0.28, -width * 0.16, -height * 0.6, sway * 0.6, -height * 0.7);
    c.bezierCurveTo(width * 0.16, -height * 0.6, width * 0.42, -height * 0.28, width * 0.27, 0);
    c.closePath();
  });
  ctx.restore();
}

export function drawFace(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  rng: Random,
  expression: Expression,
  blink: boolean,
): void {
  const eyeDx = radius * 0.42;
  const eyeY = cy - radius * 0.18;
  const eyeR = radius * 0.12;
  const inkStyle: SketchStyle = { color: Palette.ink, width: Math.max(2, radius * 0.045) };

  const openEye = (side: number, size: number, pupil: number): void => {
    const x = cx + side * eyeDx;
    sketchFill(ctx, { color: Palette.cream, alpha: 1 }, (c) => wobblyCirclePath(c, x, eyeY, size, rng, 0.7, 18));
    sketchStroke(ctx, rng, inkStyle, (c) => wobblyCirclePath(c, x, eyeY, size, rng, 0.7, 18), 1);
    ctx.save();
    ctx.fillStyle = css(Palette.ink, 0.92);
    wobblyCirclePath(ctx, x + rng.floating(-1.5, 1.5), eyeY + size * 0.22, size * pupil, rng, 0.4, 12);
    ctx.fill();
    ctx.fillStyle = css(Palette.cream, 0.95);
    ctx.beginPath();
    ctx.arc(x - size * 0.18, eyeY - size * 0.16, size * 0.15, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const arcEyes = (happyArc: boolean): void => {
    for (const side of [-1, 1]) {
      if (happyArc) {
        sketchStroke(ctx, rng, inkStyle, (c) =>
          wobblyArcPath(c, cx + side * eyeDx, eyeY + eyeR * 0.6, eyeR * 1.15, Math.PI * 1.12, Math.PI * 1.88, rng, 0.8, 10), 1);
      } else {
        sketchStroke(ctx, rng, inkStyle, (c) =>
          wobblyArcPath(c, cx + side * eyeDx, eyeY - eyeR * 0.4, eyeR, Math.PI * 0.12, Math.PI * 0.88, rng, 0.8, 8), 1);
      }
    }
  };

  const spiralEye = (side: number): void => {
    const x = cx + side * eyeDx;
    ctx.save();
    ctx.strokeStyle = css(Palette.ink, 0.9);
    ctx.lineWidth = Math.max(1.5, radius * 0.028);
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let i = 0; i <= 30; i++) {
      const t = i / 30;
      const angle = t * Math.PI * 3.1;
      const rr = t * eyeR * 1.3;
      const px = x + Math.cos(angle) * rr;
      const py = eyeY + Math.sin(angle) * rr;
      if (i === 0) {
        ctx.moveTo(px, py);
      } else {
        ctx.lineTo(px, py);
      }
    }
    ctx.stroke();
    ctx.restore();
  };

  const squintEyes = (): void => {
    for (const side of [-1, 1]) {
      sketchStroke(ctx, rng, inkStyle, (c) =>
        wobblyArcPath(c, cx + side * eyeDx, eyeY + eyeR * 0.55, eyeR * 1.2, Math.PI * 1.08, Math.PI * 1.92, rng, 0.8, 10), 1);
      sketchStroke(ctx, rng, { color: Palette.ink, width: Math.max(1.6, radius * 0.03), alpha: 0.8 }, (c) =>
        wobblyLinePath(
          c,
          cx + side * (eyeDx + eyeR * 1.05),
          eyeY + eyeR * 0.1,
          cx + side * (eyeDx + eyeR * 1.7),
          eyeY - eyeR * 0.4,
          rng,
          0.8,
          3,
        ), 1);
    }
  };

  const buckMouth = (scale: number, withNose: boolean): void => {
    const mouthR = radius * 0.34 * scale;
    const vertical = scale < 1 ? 1.0 : 1.25;
    const midY = cy + radius * 0.36;

    ctx.save();
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = css(Palette.pink, 1);
    wobblyCirclePath(ctx, cx, midY, mouthR * 1.3, rng, radius * 0.05, 26);
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.translate(cx, midY);
    ctx.scale(scale < 1 ? 1.18 : 1, vertical);
    sketchFill(ctx, { color: Palette.mouth, alpha: 0.97 }, (c) =>
      wobblyCirclePath(c, 0, 0, mouthR, rng, radius * 0.018, 40));
    ctx.restore();

    if (withNose) {
      ctx.save();
      ctx.fillStyle = css(Palette.pinkDeep, 0.9);
      wobblyCirclePath(ctx, cx, midY - mouthR * vertical - radius * 0.05, radius * 0.09, rng, radius * 0.02, 14);
      ctx.fill();
      ctx.restore();
    }
  };

  const squiggleMouth = (): void => {
    sketchStroke(ctx, rng, inkStyle, (c) => {
      c.beginPath();
      const steps = 12;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const x = cx - radius * 0.3 + t * radius * 0.6;
        const y = cy + radius * 0.3 + Math.sin(t * Math.PI * 3) * radius * 0.06;
        if (i === 0) {
          c.moveTo(x, y);
        } else {
          c.lineTo(x, y);
        }
      }
    }, 1);
  };

  const winkEyes = (): void => {
    openEye(-1, eyeR, 0.44);
    sketchStroke(ctx, rng, inkStyle, (c) =>
      wobblyArcPath(c, cx + eyeDx, eyeY + eyeR * 0.55, eyeR * 1.15, Math.PI * 1.1, Math.PI * 1.9, rng, 0.8, 10), 1);
  };

  const derpEyes = (): void => {
    for (const side of [-1, 1]) {
      const x = cx + side * eyeDx;
      sketchFill(ctx, { color: Palette.cream, alpha: 1 }, (c) => wobblyCirclePath(c, x, eyeY, eyeR * 1.1, rng, 0.7, 18));
      sketchStroke(ctx, rng, inkStyle, (c) => wobblyCirclePath(c, x, eyeY, eyeR * 1.1, rng, 0.7, 18), 1);
      ctx.save();
      ctx.fillStyle = css(Palette.ink, 0.92);
      wobblyCirclePath(ctx, x + side * eyeR * 0.5, eyeY + eyeR * 0.32, eyeR * 0.46, rng, 0.4, 12);
      ctx.fill();
      ctx.restore();
    }
  };

  const tongueOut = (): void => {
    sketchStroke(ctx, rng, inkStyle, (c) =>
      wobblyArcPath(c, cx, cy + radius * 0.16, radius * 0.3, Math.PI * 0.18, Math.PI * 0.82, rng, 1, 14), 1);
    ctx.save();
    ctx.fillStyle = css(Palette.pinkDeep, 0.95);
    wobblyCirclePath(ctx, cx + radius * 0.02, cy + radius * 0.31, radius * 0.16, rng, 1.4, 20);
    ctx.fill();
    ctx.strokeStyle = css(Palette.pinkDeep.darken(0.15), 0.8);
    ctx.lineWidth = Math.max(1.6, radius * 0.028);
    ctx.stroke();
    ctx.restore();
    sketchStroke(ctx, rng, { color: Palette.pinkDeep.darken(0.3), width: Math.max(1.6, radius * 0.03), alpha: 0.9 }, (c) =>
      wobblyLinePath(c, cx + radius * 0.02, cy + radius * 0.24, cx + radius * 0.02, cy + radius * 0.4, rng, radius * 0.012, 3), 1);
  };

  const smirkMouth = (): void => {
    sketchStroke(ctx, rng, inkStyle, (c) =>
      wobblyArcPath(c, cx + radius * 0.06, cy + radius * 0.2, radius * 0.26, Math.PI * 0.22, Math.PI * 0.78, rng, 1, 12), 1);
  };

  if (blink) {
    arcEyes(false);
  } else if (expression === 'happy') {
    arcEyes(true);
  } else if (expression === 'dizzy') {
    spiralEye(-1);
    spiralEye(1);
  } else if (expression === 'wow') {
    openEye(-1, eyeR * 1.4, 0.28);
    openEye(1, eyeR * 1.4, 0.28);
  } else if (expression === 'grit') {
    openEye(-1, eyeR * 0.95, 0.5);
    openEye(1, eyeR * 0.95, 0.5);
    const lidStyle: SketchStyle = { color: Palette.ink, width: Math.max(2, radius * 0.04), alpha: 0.8 };
    sketchStroke(ctx, rng, lidStyle, (c) =>
      wobblyLinePath(c, cx - eyeDx - eyeR * 1.05, eyeY - eyeR * 1.6, cx - eyeDx + eyeR * 0.9, eyeY - eyeR * 1.4, rng, 1, 4), 1);
    sketchStroke(ctx, rng, lidStyle, (c) =>
      wobblyLinePath(c, cx + eyeDx - eyeR * 0.9, eyeY - eyeR * 1.4, cx + eyeDx + eyeR * 1.05, eyeY - eyeR * 1.6, rng, 1, 4), 1);
  } else if (expression === 'fire') {
    squintEyes();
  } else if (expression === 'open') {
    openEye(-1, eyeR, 0.44);
    openEye(1, eyeR, 0.44);
  } else if (expression === 'tongue') {
    openEye(-1, eyeR, 0.44);
    openEye(1, eyeR, 0.44);
  } else if (expression === 'wink') {
    winkEyes();
  } else if (expression === 'derp') {
    derpEyes();
  } else if (expression === 'sad') {
    openEye(-1, eyeR, 0.42);
    openEye(1, eyeR, 0.42);
    const browStyle: SketchStyle = { color: Palette.ink, width: Math.max(2.5, radius * 0.05), alpha: 0.75 };
    sketchStroke(ctx, rng, browStyle, (c) =>
      wobblyLinePath(c, cx - eyeDx - eyeR, eyeY - eyeR * 2.3, cx - eyeDx + eyeR * 0.9, eyeY - eyeR * 1.9, rng, 1.2, 4), 1);
    sketchStroke(ctx, rng, browStyle, (c) =>
      wobblyLinePath(c, cx + eyeDx + eyeR, eyeY - eyeR * 2.3, cx + eyeDx - eyeR * 0.9, eyeY - eyeR * 1.9, rng, 1.2, 4), 1);
  } else {
    openEye(-1, eyeR, 0.44);
    openEye(1, eyeR, 0.44);
  }

  for (const side of [-1, 1]) {
    ctx.save();
    ctx.globalAlpha = expression === 'fire' ? 0.6 : 0.45;
    ctx.fillStyle = css(expression === 'fire' ? Palette.pinkDeep : Palette.pink, 1);
    wobblyCirclePath(ctx, cx + side * radius * 0.68, cy + radius * 0.14, radius * 0.13, rng, 1.4, 16);
    ctx.fill();
    ctx.restore();
  }

  if (expression === 'sad') {
    sketchStroke(ctx, rng, inkStyle, (c) =>
      wobblyArcPath(c, cx, cy + radius * 0.46, radius * 0.3, Math.PI * 1.2, Math.PI * 1.8, rng, 1, 12), 1);
  } else if (expression === 'happy') {
    sketchStroke(ctx, rng, inkStyle, (c) =>
      wobblyArcPath(c, cx, cy + radius * 0.16, radius * 0.42, Math.PI * 0.14, Math.PI * 0.86, rng, 1.2, 16), 1);
    sketchStroke(ctx, rng, inkStyle, (c) =>
      wobblyArcPath(c, cx, cy + radius * 0.1, radius * 0.2, Math.PI * 0.3, Math.PI * 0.7, rng, 0.8, 8), 1);
  } else if (expression === 'wow') {
    sketchFill(ctx, { color: Palette.ink, alpha: 0.88 }, (c) =>
      wobblyCirclePath(c, cx, cy + radius * 0.32, radius * 0.17, rng, 1, 16));
    sketchStroke(ctx, rng, inkStyle, (c) =>
      wobblyCirclePath(c, cx, cy + radius * 0.32, radius * 0.17, rng, 1, 16), 1);
    ctx.save();
    ctx.fillStyle = css(Palette.pinkDeep, 0.9);
    ctx.beginPath();
    ctx.arc(cx, cy + radius * 0.38, radius * 0.08, 0, Math.PI);
    ctx.fill();
    ctx.restore();
  } else if (expression === 'grin') {
    const halfW = radius * 0.46;
    const top = cy + radius * 0.16;
    const bulge = radius * 0.27;
    const mouthPath = (c: CanvasRenderingContext2D): void => {
      c.beginPath();
      const steps = 20;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const x = cx - halfW + t * halfW * 2;
        const y = top + Math.sin(t * Math.PI) * bulge + rng.floating(-1.2, 1.2);
        if (i === 0) {
          c.moveTo(x, y);
        } else {
          c.lineTo(x, y);
        }
      }
      for (let i = steps; i >= 0; i--) {
        const t = i / steps;
        c.lineTo(cx - halfW + t * halfW * 2, top + rng.floating(-0.8, 0.8));
      }
      c.closePath();
    };
    sketchFill(ctx, { color: Palette.mouth, alpha: 0.95 }, mouthPath);
    sketchStroke(ctx, rng, { color: Palette.ink, width: Math.max(2.2, radius * 0.04) }, mouthPath, 2, 1.2);

    const teeth = 5;
    const toothH = radius * 0.105;
    const toothW = (halfW * 1.72) / (teeth * 1.3);
    for (let i = 0; i < teeth; i++) {
      const toothX = cx - halfW * 0.86 + (i + 0.5) * ((halfW * 1.72) / teeth);
      ctx.save();
      ctx.translate(toothX, top + toothH * 0.42);
      ctx.rotate(toRadians(rng.floating(-3, 3)));
      ctx.fillStyle = css(Palette.tooth, 1);
      ctx.fillRect(-toothW / 2, -toothH / 2, toothW, toothH);
      ctx.lineWidth = 1.4;
      ctx.strokeStyle = css(Palette.deepInk, 0.35);
      ctx.strokeRect(-toothW / 2, -toothH / 2, toothW, toothH);
      ctx.restore();
    }
  } else if (expression === 'grit') {
    const halfW = radius * 0.34;
    const midY = cy + radius * 0.22;
    sketchStroke(ctx, rng, { color: Palette.ink, width: Math.max(2.6, radius * 0.05) }, (c) => {
      c.beginPath();
      const teeth = 5;
      const step = (halfW * 2) / teeth;
      for (let i = 0; i <= teeth; i++) {
        const x = cx - halfW + i * step;
        const dip = Math.sin((i / teeth) * Math.PI) * radius * 0.03;
        const y = midY + dip + (i % 2 === 0 ? -radius * 0.045 : radius * 0.045);
        if (i === 0) {
          c.moveTo(x, y);
        } else {
          c.lineTo(x, y);
        }
      }
    }, 1);
  } else if (expression === 'dizzy') {
    squiggleMouth();
  } else if (expression === 'derp') {
    squiggleMouth();
  } else if (expression === 'tongue') {
    tongueOut();
  } else if (expression === 'wink') {
    smirkMouth();
  } else if (expression === 'fire') {
    buckMouth(1, true);
  } else if (expression === 'open') {
    buckMouth(0.92, false);
  } else {
    sketchStroke(ctx, rng, inkStyle, (c) =>
      wobblyArcPath(c, cx, cy + radius * 0.18, radius * 0.32, Math.PI * 0.2, Math.PI * 0.8, rng, 1, 14), 1);
  }
}

export interface ClockBaseOptions {
  radius: number;
  faceLight: Color;
  faceShade: Color;
  rim: Color;
  tick: Color;
  accents: Color[];
}

export function paintClockBase(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rng: Random,
  options: ClockBaseOptions,
): void {
  const radius = options.radius;
  const gradient = ctx.createRadialGradient(
    cx - radius * 0.3,
    cy - radius * 0.35,
    radius * 0.15,
    cx,
    cy,
    radius * 1.15,
  );
  gradient.addColorStop(0, css(options.faceLight));
  gradient.addColorStop(1, css(options.faceShade));
  ctx.save();
  ctx.fillStyle = gradient;
  wobblyCirclePath(ctx, cx, cy, radius - 6, rng, 4, 72);
  ctx.fill();
  ctx.restore();

  let accentStart = rng.floating(0, Math.PI * 2);
  for (let i = 0; i < options.accents.length; i++) {
    const span = rng.floating(0.7, 1.5);
    const color = options.accents[i];
    const accentRadius = radius - rng.floating(16, 24);
    sketchStroke(ctx, rng, { color, width: rng.floating(7, 11), alpha: 0.4 }, (c) =>
      wobblyArcPath(c, cx, cy, accentRadius, accentStart, accentStart + span, rng, 2.5, 40), 1);
    accentStart += span + rng.floating(0.35, 1.1);
  }

  sketchStroke(ctx, rng, { color: options.rim, width: 5.5 }, (c) =>
    wobblyCirclePath(c, cx, cy, radius, rng, 4.5, 84), 2, 2.2);

  for (let hour = 0; hour < 12; hour++) {
    const angle = (hour / 12) * Math.PI * 2 + rng.floating(-0.02, 0.02);
    const inner = radius - rng.floating(40, 48);
    const outer = radius - rng.floating(12, 18);
    const x1 = cx + Math.cos(angle) * inner;
    const y1 = cy + Math.sin(angle) * inner;
    const x2 = cx + Math.cos(angle) * outer;
    const y2 = cy + Math.sin(angle) * outer;
    sketchStroke(ctx, rng, { color: options.tick, width: hour % 3 === 0 ? 6 : 3.5, alpha: 0.85 }, (c) =>
      wobblyLinePath(c, x1, y1, x2, y2, rng, 1.2, 4), 1);
  }
}

export interface MiniClockMarker {
  angle: number;
  color: Color;
}

export interface MiniClockOptions {
  radius: number;
  minuteAngle: number;
  hourAngle: number;
  markers?: MiniClockMarker[];
  face?: Expression;
  blink?: boolean;
}

export function paintMiniClock(
  ctx: CanvasRenderingContext2D,
  rng: Random,
  options: MiniClockOptions,
): void {
  const radius = options.radius;
  paintClockBase(ctx, 0, 0, rng, {
    radius,
    faceLight: Palette.cream,
    faceShade: Palette.paperDeep,
    rim: Palette.ink,
    tick: Palette.ink,
    accents: [Palette.mint, Palette.yellow, Palette.pink, Palette.blue],
  });

  const markerRadius = radius - 4;
  for (const marker of options.markers ?? []) {
    const x = Math.sin(toRadians(marker.angle)) * markerRadius;
    const y = -Math.cos(toRadians(marker.angle)) * markerRadius;
    ctx.save();
    ctx.fillStyle = css(marker.color, 0.9);
    wobblyCirclePath(ctx, x, y, 15, rng, 1.4, 22);
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  ctx.rotate(toRadians(options.minuteAngle));
  drawTaperedStroke(ctx, rng, 0, 22, -(radius * 0.78), radius * 0.05, Palette.blue, Palette.blueDeep);
  ctx.restore();

  ctx.save();
  ctx.rotate(toRadians(options.hourAngle));
  drawTaperedStroke(ctx, rng, 0, 20, -(radius * 0.56), radius * 0.066, Palette.pink, Palette.pinkDeep);
  ctx.restore();

  ctx.save();
  ctx.fillStyle = css(Palette.cream, 1);
  wobblyCirclePath(ctx, 0, 0, 15, rng, 1.1, 20);
  ctx.fill();
  ctx.lineWidth = 2.6;
  ctx.strokeStyle = css(Palette.ink, 0.9);
  ctx.stroke();
  ctx.restore();

  drawFace(ctx, 0, 0, radius * 0.56, rng, options.face ?? 'idle', options.blink === true);
}

export type DoodleKind =
  | 'star'
  | 'spiral'
  | 'flower'
  | 'heart'
  | 'cloud'
  | 'sun'
  | 'squiggle'
  | 'crescent'
  | 'leaf'
  | 'zigzag';

export const DOODLE_KINDS: DoodleKind[] = [
  'star',
  'spiral',
  'flower',
  'heart',
  'cloud',
  'sun',
  'squiggle',
  'crescent',
  'leaf',
  'zigzag',
];

export function drawDoodle(
  ctx: CanvasRenderingContext2D,
  kind: DoodleKind,
  cx: number,
  cy: number,
  size: number,
  rng: Random,
  color: Color,
  width = 3.4,
): void {
  const stroke: SketchStyle = { color, width, alpha: 0.9 };

  switch (kind) {
    case 'star': {
      const path = (c: CanvasRenderingContext2D): void => {
        const points = 5;
        c.beginPath();
        for (let i = 0; i <= points * 2; i++) {
          const angle = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
          const radius = (i % 2 === 0 ? size : size * 0.46) + rng.floating(-size * 0.07, size * 0.07);
          const px = cx + Math.cos(angle) * radius;
          const py = cy + Math.sin(angle) * radius;
          if (i === 0) {
            c.moveTo(px, py);
          } else {
            c.lineTo(px, py);
          }
        }
        c.closePath();
      };
      sketchFill(ctx, { color, alpha: 0.28 }, path);
      sketchStroke(ctx, rng, stroke, path, 2, 1);
      break;
    }
    case 'spiral': {
      const path = (c: CanvasRenderingContext2D): void => {
        c.beginPath();
        const turns = 2.6;
        const steps = 60;
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          const angle = t * Math.PI * 2 * turns;
          const radius = size * 0.12 + t * size * 0.88 + rng.floating(-0.6, 0.6);
          const px = cx + Math.cos(angle) * radius;
          const py = cy + Math.sin(angle) * radius;
          if (i === 0) {
            c.moveTo(px, py);
          } else {
            c.lineTo(px, py);
          }
        }
      };
      sketchStroke(ctx, rng, stroke, path, 2, 1);
      break;
    }
    case 'flower': {
      const petals = 5;
      for (let i = 0; i < petals; i++) {
        const angle = (i / petals) * Math.PI * 2 + rng.floating(-0.1, 0.1);
        const px = cx + Math.cos(angle) * size * 0.55;
        const py = cy + Math.sin(angle) * size * 0.55;
        sketchFill(ctx, { color, alpha: 0.22 }, (c) =>
          wobblyCirclePath(c, px, py, size * 0.36, rng, size * 0.05, 20));
        sketchStroke(ctx, rng, stroke, (c) =>
          wobblyCirclePath(c, px, py, size * 0.36, rng, size * 0.05, 20), 1, 1);
      }
      sketchFill(ctx, { color: Palette.yellowDeep, alpha: 0.5 }, (c) =>
        wobblyCirclePath(c, cx, cy, size * 0.2, rng, 1.2, 16));
      sketchStroke(ctx, rng, stroke, (c) => wobblyCirclePath(c, cx, cy, size * 0.2, rng, 1.2, 16), 1, 1);
      break;
    }
    case 'heart':
      drawHeart(ctx, cx, cy, size * 1.5, rng, color, color);
      break;
    case 'cloud': {
      const blobs = [
        { x: cx - size * 0.5, y: cy + size * 0.1, radius: size * 0.34 },
        { x: cx, y: cy - size * 0.18, radius: size * 0.44 },
        { x: cx + size * 0.52, y: cy + size * 0.08, radius: size * 0.32 },
      ];
      for (const blob of blobs) {
        sketchFill(ctx, { color, alpha: 0.2 }, (c) =>
          wobblyCirclePath(c, blob.x, blob.y, blob.radius, rng, size * 0.05, 20));
        sketchStroke(ctx, rng, stroke, (c) =>
          wobblyCirclePath(c, blob.x, blob.y, blob.radius, rng, size * 0.05, 20), 1, 1);
      }
      sketchStroke(ctx, rng, stroke, (c) =>
        wobblyLinePath(c, cx - size * 0.78, cy + size * 0.34, cx + size * 0.8, cy + size * 0.32, rng, 1.5, 6), 1, 1);
      break;
    }
    case 'sun': {
      sketchFill(ctx, { color, alpha: 0.25 }, (c) =>
        wobblyCirclePath(c, cx, cy, size * 0.44, rng, size * 0.05, 24));
      sketchStroke(ctx, rng, stroke, (c) =>
        wobblyCirclePath(c, cx, cy, size * 0.44, rng, size * 0.05, 24), 1, 1);
      const rays = 8;
      for (let i = 0; i < rays; i++) {
        const angle = (i / rays) * Math.PI * 2 + rng.floating(-0.06, 0.06);
        const inner = size * 0.62;
        const outer = size * (0.95 + rng.floating(-0.08, 0.12));
        sketchStroke(
          ctx,
          rng,
          stroke,
          (c) =>
            wobblyLinePath(
              c,
              cx + Math.cos(angle) * inner,
              cy + Math.sin(angle) * inner,
              cx + Math.cos(angle) * outer,
              cy + Math.sin(angle) * outer,
              rng,
              1,
              3,
            ),
          1,
          1,
        );
      }
      break;
    }
    case 'squiggle': {
      const path = (c: CanvasRenderingContext2D): void => {
        c.beginPath();
        const steps = 40;
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          const px = cx - size + t * size * 2;
          const py = cy + Math.sin(t * Math.PI * 2.4) * size * 0.42 + rng.floating(-0.5, 0.5);
          if (i === 0) {
            c.moveTo(px, py);
          } else {
            c.lineTo(px, py);
          }
        }
      };
      sketchStroke(ctx, rng, stroke, path, 2, 1.2);
      break;
    }
    case 'crescent': {
      sketchFill(ctx, { color, alpha: 0.22 }, (c) =>
        wobblyCirclePath(c, cx, cy, size * 0.88, rng, size * 0.05, 28));
      sketchStroke(ctx, rng, stroke, (c) =>
        wobblyArcPath(c, cx, cy, size * 0.9, Math.PI * 0.28, Math.PI * 1.72, rng, 2, 32), 1, 1);
      sketchStroke(ctx, rng, stroke, (c) =>
        wobblyArcPath(c, cx + size * 0.42, cy, size * 0.86, Math.PI * 1.65, Math.PI * 0.35, rng, 2, 32), 1, 1);
      break;
    }
    case 'leaf': {
      const path = (c: CanvasRenderingContext2D): void => {
        c.beginPath();
        const steps = 24;
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          const py = cy - size * 0.9 + t * size * 1.8;
          const half = size * 0.55 * Math.sin(Math.PI * t) + rng.floating(-0.5, 0.5);
          if (i === 0) {
            c.moveTo(cx + half, py);
          } else {
            c.lineTo(cx + half, py);
          }
        }
        for (let i = steps; i >= 0; i--) {
          const t = i / steps;
          const py = cy - size * 0.9 + t * size * 1.8;
          const half = -(size * 0.55 * Math.sin(Math.PI * t) + rng.floating(-0.5, 0.5));
          c.lineTo(cx + half, py);
        }
        c.closePath();
      };
      sketchFill(ctx, { color, alpha: 0.22 }, path);
      sketchStroke(ctx, rng, stroke, path, 2, 1);
      sketchStroke(ctx, rng, { color, width: width * 0.7, alpha: 0.7 }, (c) =>
        wobblyLinePath(c, cx, cy - size * 0.8, cx, cy + size * 0.8, rng, 1.2, 6), 1);
      break;
    }
    case 'zigzag': {
      const path = (c: CanvasRenderingContext2D): void => {
        const pts: Array<[number, number]> = [
          [cx - size * 0.35, cy - size],
          [cx + size * 0.3, cy - size * 0.15],
          [cx - size * 0.15, cy - size * 0.05],
          [cx + size * 0.4, cy + size],
        ];
        c.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const px = pts[i][0] + rng.floating(-1, 1);
          const py = pts[i][1] + rng.floating(-1, 1);
          if (i === 0) {
            c.moveTo(px, py);
          } else {
            c.lineTo(px, py);
          }
        }
      };
      sketchStroke(ctx, rng, { color: Palette.yellowDeep, width }, path, 2, 1);
      break;
    }
    default: {
      sketchStroke(ctx, rng, stroke, (c) => wobblyCirclePath(c, cx, cy, size * 0.5, rng, 2, 20), 2);
      break;
    }
  }
}
